import React, { useEffect, useState } from 'react';
import { Box, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import InputMask from 'react-input-mask';
import { useTranslation } from 'react-i18next';
import { SCUIAutoCompleteInput, SCUIButton } from '../../../../../atomic/atoms';
import { ReturnQuery } from '../../../../../services/query';
import Helper from '../../../../../utils/helper';
import {
    FieldConfig,
    validateReturnCardField,
    ValidationRuleType,
} from '../returnCardFieldValidation.helper';
import {
    extractCountryListFromPageRulesPayload,
    setReturnPageRulesCountryListCache,
} from '../returnPageRules.helper';
import { RCADC } from './returnCardAddDrawerContent.classes';

export { ValidationRuleType };

export const ReturnCardAddDrawerContent = (props: any) => {
    const { t } = useTranslation();
    const [bankConfig, setBankConfig] = useState<FieldConfig[]>([]);
    const [countries, setCountries] = useState<any[]>([]);
    const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
    const CountryId = Helper.GetCurrentCountry();

    const getFieldsForCountry = (countriesData: any[], countryId: number): FieldConfig[] => {
        if (!countriesData?.length) return [];
        const matchedCountry = countriesData.find((c) => c.countryId === countryId);
        return matchedCountry?.fields || countriesData[0]?.fields || [];
    };

    const fetchPageRules = async (cId?: number) => {
        try {
            const requestCountryId = cId !== undefined ? cId : CountryId;
            const response = await ReturnQuery.GetPageRulesList({ CountryId: requestCountryId });
            const payload = response?.data || response;

            const countryList = extractCountryListFromPageRulesPayload(payload);

            setCountries(countryList);
            setReturnPageRulesCountryListCache(countryList);

            const countriesData = payload?.countries || [];
            let activeCountryId = cId !== undefined ? cId : requestCountryId;

            if (cId === undefined) {
                if (countryList.length > 0) {
                    const contextCountryInList = countryList.find((c: any) => c.countryId === requestCountryId);
                    activeCountryId = contextCountryInList ? requestCountryId : countryList[0].countryId;
                } else if (countriesData.length > 0) {
                    activeCountryId = countriesData[0].countryId;
                }

                setSelectedCountryId(activeCountryId);

                if (activeCountryId !== requestCountryId) {
                    await fetchPageRules(activeCountryId);
                    return;
                }
            } else {
                setSelectedCountryId(cId);
            }

            let fields = getFieldsForCountry(countriesData, activeCountryId);

            if (fields.length === 0 && payload?.fields) {
                fields = payload.fields;
            }

            if (cId === undefined && fields.length === 0 && countryList.length > 1) {
                await fetchPageRules(activeCountryId);
                return;
            }

            const sortedFields =
                fields.length > 0
                    ? [...fields].sort(
                          (a: FieldConfig, b: FieldConfig) => (a.displayOrder || 0) - (b.displayOrder || 0)
                      )
                    : [];

            setBankConfig(sortedFields);
        } catch (error) {
            console.error('Error fetching page rules:', error);
            setBankConfig([]);
        }
    };

    useEffect(() => {
        fetchPageRules();
    }, []);

    const handleCountryChange = (event: any) => {
        const newCountryId = event.target.value;
        setSelectedCountryId(newCountryId);
        fetchPageRules(newCountryId);
    };

    const validateField = (field: FieldConfig, value: string): string =>
        validateReturnCardField(field, value, t);

    const validateAllFields = (): boolean => {
        const errors: { [key: string]: string } = {};
        let isValid = true;

        bankConfig.forEach((field) => {
            const value = props.insertBankAccountRequest[field.fieldName] || '';
            const error = validateField(field, value);

            if (error) {
                errors[field.fieldName] = error;
                isValid = false;
            }
        });

        setValidationErrors(errors);
        return isValid;
    };

    const handleFieldChange = (fieldName: string, value: string) => {
        let processedValue = value;

        const field = bankConfig.find((f) => f.fieldName === fieldName);

        if (fieldName === 'accountNumber' && !field?.fieldPattern) {
            processedValue = value.replace(/[^0-9]/g, '');
        }

        props.setInsertBankAccountRequest({
            ...props.insertBankAccountRequest,
            [fieldName]: processedValue,
        });

        if (field) {
            const error = validateField(field, processedValue);
            setValidationErrors((prev) => ({
                ...prev,
                [fieldName]: error,
            }));
        }
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        if (validateAllFields()) {
            props.InsertBankAccount();
        }
    };

    const isFormValid = (): boolean => {
        if (bankConfig.length === 0) return false;

        const hasAllRequiredFields = bankConfig.every((field) => {
            if (!field.isRequired) return true;
            const value = props.insertBankAccountRequest[field.fieldName];

            if (field.isSelection) {
                return value !== undefined && value !== null && value !== '';
            }

            return value && value.toString().trim() !== '';
        });

        const allFieldsValid = bankConfig.every((field) => {
            const value = props.insertBankAccountRequest[field.fieldName] || '';
            return validateField(field, value.toString()) === '';
        });

        return hasAllRequiredFields && allFieldsValid;
    };

    const renderField = (field: FieldConfig) => {
        const value = props.insertBankAccountRequest[field.fieldName] || '';
        const errorMessage = validationErrors[field.fieldName] || '';
        const hasError = errorMessage !== '';

        if (field.isSelection) {
            return (
                <div
                    key={field.fieldName}
                    className={RCADC.fieldWrap}
                    data-field={field.fieldName}
                    data-field-type="selection"
                >
                    <div className={RCADC.fieldAutocompleteWrap} data-field={field.fieldName}>
                        <SCUIAutoCompleteInput
                            key={`${field.fieldName}-${selectedCountryId ?? 'default'}`}
                            list={field.fieldValues || []}
                            onChange={(event: any, newValue: any) => {
                                handleFieldChange(field.fieldName, newValue?.value || '');
                            }}
                            multiple={false}
                            txtPlaceHolder={t(`return.${field.fieldName}`)}
                            searchValue="showedText"
                        />
                    </div>
                    {errorMessage && (
                        <FormHelperText className={RCADC.fieldHelperText} error sx={{ ml: 2, mt: 0.5 }}>
                            {errorMessage}
                        </FormHelperText>
                    )}
                </div>
            );
        }

        if (field.fieldPattern) {
            return (
                <InputMask
                    key={field.fieldName}
                    mask={field.fieldPattern.replace(/#/g, '*')}
                    value={value}
                    onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
                    maskChar={null}
                >
                    {(inputProps: any) => (
                        <TextField
                            {...inputProps}
                            className={RCADC.fieldText}
                            data-field={field.fieldName}
                            data-field-type="text"
                            label={t(`return.${field.fieldName}`)}
                            fullWidth
                            required={field.isRequired}
                            error={hasError}
                            helperText={errorMessage}
                            slotProps={{
                                htmlInput:
                                    field.fieldName === 'accountNumber'
                                        ? {
                                              inputMode: 'numeric',
                                              pattern: '[0-9]*',
                                          }
                                        : field.fieldName === 'iban'
                                          ? {
                                                style: { textTransform: 'uppercase' },
                                            }
                                          : {},
                            }}
                            sx={
                                hasError
                                    ? {
                                          '& .MuiOutlinedInput-root': {
                                              backgroundColor: '#ffebee',
                                              '&:hover': {
                                                  backgroundColor: '#ffcdd2',
                                              },
                                          },
                                          '& .MuiFormHelperText-root': {
                                              fontSize: '11px',
                                              fontWeight: 'bold',
                                          },
                                      }
                                    : {}
                            }
                        />
                    )}
                </InputMask>
            );
        }

        return (
            <TextField
                key={field.fieldName}
                className={RCADC.fieldText}
                data-field={field.fieldName}
                data-field-type="text"
                label={t(`return.${field.fieldName}`)}
                fullWidth
                required={field.isRequired}
                value={value}
                onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
                error={hasError}
                helperText={errorMessage}
                slotProps={{
                    htmlInput:
                        field.fieldName === 'accountNumber'
                            ? {
                                  inputMode: 'numeric',
                                  pattern: '[0-9]*',
                              }
                            : field.fieldName === 'iban'
                              ? {
                                    style: { textTransform: 'uppercase' },
                                }
                              : {},
                }}
                sx={
                    hasError
                        ? {
                              '& .MuiOutlinedInput-root': {
                                  backgroundColor: '#ffebee',
                                  '&:hover': {
                                      backgroundColor: '#ffcdd2',
                                  },
                              },
                              '& .MuiFormHelperText-root': {
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                              },
                          }
                        : {}
                }
            />
        );
    };

    return (
        <Box
            className={RCADC.root}
            sx={{ width: '100%', justifyContent: 'center', alignItems: 'center', display: 'flex' }}
        >
            <Box className={RCADC.outer} sx={{ padding: '8px', width: '100%', pb: 3 }}>
                <form className={RCADC.form} onSubmit={handleSubmit}>
                    <Box sx={{ width: '100%', padding: '8px' }} className={RCADC.container}>
                        <Grid
                            container
                            className={RCADC.fieldsLayout}
                            sx={{
                                lineHeight: '6rem',
                                width: '100%',
                            }}
                        >
                            {countries.length > 1 && (
                                <Grid
                                    className={RCADC.fieldGridItem}
                                    size={{ xl: 12, lg: 12, md: 12, xs: 12 }}
                                    sx={{ mb: 2 }}
                                >
                                    <FormControl fullWidth>
                                        <InputLabel>{t('general.country')}</InputLabel>
                                        <Select
                                            value={selectedCountryId || ''}
                                            label={t('general.country')}
                                            onChange={handleCountryChange}
                                        >
                                            {countries.map((c: any) => (
                                                <MenuItem key={c.countryId} value={c.countryId}>
                                                    {c.countryName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            {bankConfig.map((field) => (
                                <Grid
                                    key={field.fieldName}
                                    className={RCADC.fieldGridItem}
                                    size={{
                                        xl: 12,
                                        lg: 12,
                                        md: 12,
                                        xs: 12,
                                    }}
                                >
                                    {renderField(field)}
                                </Grid>
                            ))}
                            <Grid
                                className={RCADC.submitRow}
                                size={{ xl: 12, lg: 12, md: 12, xs: 12 }}
                                sx={{
                                    width: '100%',
                                    mt: 2,
                                    mb: 2,
                                }}
                            >
                                <SCUIButton
                                    className={RCADC.submitButton}
                                    text={t('general.add')}
                                    type="primary"
                                    sizeType="fixed"
                                    buttonType="submit"
                                    disabled={!isFormValid()}
                                    size={'m'}
                                    fullwidth
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </form>
            </Box>
        </Box>
    );
};
